import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Droplets, Thermometer, Plus, Trash2, Clock, Gauge, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FUEL_GRADES = ['Regular', 'Plus', 'Premium', 'Diesel'];

const GRADE_COLORS = {
  Regular: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', bar: 'bg-emerald-500' },
  Plus: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', bar: 'bg-blue-500' },
  Premium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', bar: 'bg-amber-500' },
  Diesel: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', bar: 'bg-yellow-500' },
};

const DEFAULT_CAPACITIES = {
  Regular: 10000,
  Plus: 8000,
  Premium: 8000,
  Diesel: 12000,
};

export default function ManualTankEntry() {
  const { token } = useAuth();
  const [latestReadings, setLatestReadings] = useState([]);
  const [recentEntries, setRecentEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    tank_number: 1,
    fuel_grade: 'Regular',
    volume_gallons: '',
    capacity_gallons: 10000,
    water_level_inches: 0,
    temperature_f: 65,
    notes: '',
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [latestRes, recentRes] = await Promise.all([
        axios.get(`${API_URL}/api/fuel-station/tanks/manual/latest`, { headers }),
        axios.get(`${API_URL}/api/fuel-station/tanks/manual?limit=20`, { headers }),
      ]);
      setLatestReadings(latestRes.data);
      setRecentEntries(recentRes.data);
    } catch (err) {
      console.error('Failed to fetch tank data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.volume_gallons || form.volume_gallons <= 0) {
      toast.error('Please enter a valid volume');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/fuel-station/tanks/manual`, {
        tank_number: form.tank_number,
        fuel_grade: form.fuel_grade,
        volume_gallons: parseFloat(form.volume_gallons),
        capacity_gallons: form.capacity_gallons,
        water_level_inches: form.water_level_inches,
        temperature_f: form.temperature_f,
        notes: form.notes || null,
      }, { headers });

      toast.success(`Tank ${form.tank_number} reading saved`);
      setShowForm(false);
      setForm(prev => ({ ...prev, volume_gallons: '', notes: '' }));
      fetchData();
    } catch (err) {
      toast.error('Failed to save reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/fuel-station/tanks/manual/${id}`, { headers });
      toast.success('Entry deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getStatusColor = (percent) => {
    if (percent >= 50) return 'text-emerald-400';
    if (percent >= 25) return 'text-amber-400';
    return 'text-red-400';
  };

  const getBarColor = (percent) => {
    if (percent >= 50) return 'bg-emerald-500';
    if (percent >= 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tank-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="manual-tank-entry-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100" data-testid="page-title">Tank Level Readings</h1>
          <p className="text-sm text-slate-400 mt-1">Manual tank inventory tracking (TLS-350 not networked)</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors font-medium"
          data-testid="new-reading-btn"
        >
          <Plus className="w-4 h-4" />
          New Reading
        </button>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-6" data-testid="tank-entry-form">
          <h2 className="text-lg font-semibold text-slate-200 mb-4">Record Tank Reading</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Tank #</label>
              <select
                value={form.tank_number}
                onChange={(e) => setForm(prev => ({ ...prev, tank_number: parseInt(e.target.value) }))}
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                data-testid="tank-number-select"
              >
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>Tank {n}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Fuel Grade</label>
              <select
                value={form.fuel_grade}
                onChange={(e) => {
                  const grade = e.target.value;
                  setForm(prev => ({
                    ...prev,
                    fuel_grade: grade,
                    capacity_gallons: DEFAULT_CAPACITIES[grade] || 10000,
                  }));
                }}
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                data-testid="fuel-grade-select"
              >
                {FUEL_GRADES.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Volume (gallons)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={form.volume_gallons}
                onChange={(e) => setForm(prev => ({ ...prev, volume_gallons: e.target.value }))}
                placeholder="e.g. 7500"
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500"
                required
                data-testid="volume-input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Tank Capacity (gallons)</label>
              <input
                type="number"
                step="100"
                min="100"
                value={form.capacity_gallons}
                onChange={(e) => setForm(prev => ({ ...prev, capacity_gallons: parseFloat(e.target.value) }))}
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                data-testid="capacity-input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Water Level (inches)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.water_level_inches}
                onChange={(e) => setForm(prev => ({ ...prev, water_level_inches: parseFloat(e.target.value) }))}
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                data-testid="water-level-input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Temperature (F)</label>
              <input
                type="number"
                step="0.1"
                value={form.temperature_f}
                onChange={(e) => setForm(prev => ({ ...prev, temperature_f: parseFloat(e.target.value) }))}
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                data-testid="temperature-input"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm text-slate-400 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g. After delivery, stick reading"
                className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500"
                data-testid="notes-input"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3 flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
                data-testid="save-reading-btn"
              >
                {submitting ? 'Saving...' : 'Save Reading'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                data-testid="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Current Tank Levels - Cards */}
      <div>
        <h2 className="text-lg font-semibold text-slate-200 mb-3">Current Tank Levels</h2>
        {latestReadings.length === 0 ? (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-8 text-center" data-testid="no-readings">
            <Droplets className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No readings recorded yet</p>
            <p className="text-sm text-slate-500 mt-1">Click "New Reading" to add your first tank measurement</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tank-cards-grid">
            {latestReadings.map((tank) => {
              const colors = GRADE_COLORS[tank.fuel_grade] || GRADE_COLORS.Regular;
              const pct = tank.volume_percent || 0;
              return (
                <div
                  key={tank.tank_number}
                  className={`${colors.bg} border ${colors.border} rounded-xl p-5 relative overflow-hidden`}
                  data-testid={`tank-card-${tank.tank_number}`}
                >
                  {/* Low level warning */}
                  {pct < 20 && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 text-red-400 text-xs font-medium">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      LOW
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Tank {tank.tank_number}</span>
                      <h3 className={`text-lg font-bold ${colors.text}`}>{tank.fuel_grade}</h3>
                    </div>
                    <Gauge className={`w-5 h-5 ${colors.text} opacity-60`} />
                  </div>

                  {/* Volume bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className={`font-semibold ${getStatusColor(pct)}`}>
                        {pct.toFixed(1)}%
                      </span>
                      <span className="text-slate-400">
                        {tank.volume_gallons.toLocaleString()} / {tank.capacity_gallons.toLocaleString()} gal
                      </span>
                    </div>
                    <div className="w-full bg-slate-900/50 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${getBarColor(pct)}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                    <div className="flex items-center gap-1">
                      <Droplets className="w-3 h-3" />
                      Water: {tank.water_level_inches}" 
                    </div>
                    <div className="flex items-center gap-1">
                      <Thermometer className="w-3 h-3" />
                      {tank.temperature_f}F
                    </div>
                    <div className="flex items-center gap-1 col-span-2">
                      <Clock className="w-3 h-3" />
                      {new Date(tank.recorded_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Entries Log */}
      <div>
        <h2 className="text-lg font-semibold text-slate-200 mb-3">Reading History</h2>
        {recentEntries.length === 0 ? (
          <p className="text-slate-500 text-sm">No entries yet</p>
        ) : (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl overflow-hidden" data-testid="reading-history-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50 text-slate-400 text-left">
                  <th className="px-4 py-3 font-medium">Tank</th>
                  <th className="px-4 py-3 font-medium">Grade</th>
                  <th className="px-4 py-3 font-medium">Volume</th>
                  <th className="px-4 py-3 font-medium">Level</th>
                  <th className="px-4 py-3 font-medium">Water</th>
                  <th className="px-4 py-3 font-medium">Temp</th>
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Notes</th>
                  <th className="px-4 py-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {recentEntries.map((entry, idx) => {
                  const pct = entry.volume_percent || 0;
                  return (
                    <tr
                      key={entry.id || idx}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      data-testid={`history-row-${idx}`}
                    >
                      <td className="px-4 py-3 text-slate-200 font-medium">#{entry.tank_number}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${(GRADE_COLORS[entry.fuel_grade] || GRADE_COLORS.Regular).bg} ${(GRADE_COLORS[entry.fuel_grade] || GRADE_COLORS.Regular).text}`}>
                          {entry.fuel_grade}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{entry.volume_gallons?.toLocaleString()} gal</td>
                      <td className={`px-4 py-3 font-medium ${getStatusColor(pct)}`}>{pct.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-slate-400">{entry.water_level_inches}"</td>
                      <td className="px-4 py-3 text-slate-400">{entry.temperature_f}F</td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{new Date(entry.recorded_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs max-w-[120px] truncate">{entry.notes || '-'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-slate-500 hover:text-red-400 transition-colors"
                          data-testid={`delete-entry-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
